// Core packages
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { SuppliersService } from 'src/app/modules/suppliers/suppliers.service';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from 'src/app/shared/services/config.service';
import { CreditsafeService } from 'src/app/shared/services/creditsafe.service';
import { HelperService } from 'src/app/shared/services/helper.service';
import { OpenapiService } from 'src/app/shared/services/openapi.service';

@Component({
  selector: 'app-add-supplier-dialog',
  templateUrl: './add-supplier-dialog.component.html',
  styleUrls: ['./add-supplier-dialog.component.scss']
})
export class AddSupplierDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserRole: any;
  loggedUserRoleType = '';
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add employee form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.create'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-save',
      inline: true,
    },
  };
  // END - Add employee form

  dialogIsLoading = true;

  dataFromOpenApi = false;
  suppliersFromOpenApi: any[] = [];

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<AddSupplierDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,

    private cookieService: CookieService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private suppliersService: SuppliersService,
    private authService: AuthService,
    private helperService: HelperService,
    private openapiService: OpenapiService,
    private creditsafeService: CreditsafeService,
  ) {
    dialogRef.disableClose = true;
  }

  /**
   * Init component
   *
   * @returns undefined
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => {
      this.loggedUser = res;

      const currentRole = this.loggedUser.userRoles.find((obj: any) => {
        return (
          obj._id.toString() ===
          this.authService.loggedUserRequestedRole$.getValue().toString()
        );
      });
      this.loggedUserRole = currentRole.role;
      this.loggedUserRoleType = this.loggedUserRole.type;
    }));

    // Get current lang
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    // Init add form
    this.initForm();

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @returns undefined
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = {};

    const categoriesOptions: any[] = [];
    const rawCategories = this.translateService.instant('suppliers.categories');
    for (const trans of Object.keys(rawCategories)) {
      categoriesOptions.push({
        label: rawCategories[trans],
        value: trans,
      });
    }

    // Set fields
    this.fields = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            key: "status",
            templateOptions: {
              type: "hidden",
            },
          },
          {
            key: "notRegistered",
            defaultValue: false,
            templateOptions: {
              type: "hidden",
            },
          },
          {
            key: "openApiId",
            templateOptions: {
              type: "hidden",
            },
          },
          {
            key: "startCompanyDate",
            templateOptions: {
              type: "hidden",
            },
          },
          {
            className: 'col-12',
            type: 'input',
            key: 'name',
            defaultValue: (this.data && this.data.name) ? this.data.name : '',
            templateOptions: {
              label: this.translateService.instant('suppliers.fields.name'),
              placeholder: this.translateService.instant('suppliers.fields.name'),
              required: true,
              minLength: 2,
              maxLength: 100,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'vatNumber',
            templateOptions: {
              label: this.translateService.instant('suppliers.fields.vatNumber'),
              placeholder: this.translateService.instant('suppliers.fields.vatNumber'),
              required: false,
              minLength: 0,
              maxLength: 20,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'taxCode',
            templateOptions: {
              label: this.translateService.instant('suppliers.fields.taxCode'),
              placeholder: this.translateService.instant('suppliers.fields.taxCode'),
              required: false,
              minLength: 0,
              maxLength: 20,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'address',
            key: 'legalAddress',
            templateOptions: {
              label: this.translateService.instant('suppliers.fields.legalAddress'),
              placeholder: this.translateService.instant('suppliers.fields.legalAddress'),
              required: false,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'address',
            key: 'operativeAddress',
            templateOptions: {
              label: this.translateService.instant('suppliers.fields.operativeAddress'),
              placeholder: this.translateService.instant('suppliers.fields.operativeAddress'),
              required: false,
            },
          },
          {
            className: 'col-12',
            type: 'select',
            key: 'categories',
            templateOptions: {
              label: this.translateService.instant('suppliers.fields.categories'),
              placeholder: this.translateService.instant('suppliers.fields.categories'),
              multiple: true,
              options: categoriesOptions,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'contactPerson',
            templateOptions: {
              label: this.translateService.instant('suppliers.fields.contactPerson'),
              placeholder: this.translateService.instant('suppliers.fields.contactPerson'),
              required: false,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'email',
            templateOptions: {
              label: this.translateService.instant('suppliers.fields.email'),
              placeholder: this.translateService.instant('suppliers.fields.email'),
              required: false,
              minLength: 5,
              maxLength: 320,
              type: 'email',
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'pec',
            templateOptions: {
              label: this.translateService.instant('suppliers.fields.pec'),
              placeholder: this.translateService.instant('suppliers.fields.pec'),
              required: false,
              minLength: 5,
              maxLength: 320,
              type: 'email',
            },
          },
          {
            className: 'col-12 col-md-4',
            type: 'input',
            key: 'phone',
            templateOptions: {
              label: this.translateService.instant('suppliers.fields.phone'),
              placeholder: this.translateService.instant('suppliers.fields.phone'),
              required: false,
              minLength: 1,
              maxLength: 15,
              pattern: this.configService.phonePattern,
            },
          },
          {
            className: 'col-12 col-md-4',
            type: 'input',
            key: 'fax',
            templateOptions: {
              label: this.translateService.instant('suppliers.fields.fax'),
              placeholder: this.translateService.instant('suppliers.fields.fax'),
              required: false,
              minLength: 1,
              maxLength: 15,
              pattern: this.configService.phonePattern,
            },
          },
          {
            className: 'col-12 col-md-4',
            type: 'input',
            key: 'webSiteUrl',
            templateOptions: {
              label: this.translateService.instant('suppliers.fields.webSiteUrl'),
              placeholder: this.translateService.instant('suppliers.fields.webSiteUrl'),
              required: false,
              minLength: 1,
              maxLength: 100,
              pattern: this.configService.webSitePattern,
            },
          },
        ],
      },
    ];
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   * @author Francesco Bancalari <francesco.bancalari@sitisrl.it>
   *
   * @param [data] optional data passed to the caller component
   * @returns void
   */
  onClose(data?: any): void {
    this.dialogRef.close(data);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(this.translateService.instant('common.errors.form_is_invalid'));
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    const sendData = JSON.parse(JSON.stringify(model));

    // adjust the phone type
    sendData.phone = {
      prefix: "",
      number: sendData.phone
    };

    this.subscriptions.push(
      // this.suppliersService.create(model) @TODO
      this.suppliersService.createV2(sendData)
        .pipe(
          finalize(() => this.formBtnOptions.active = false)
        )
        .subscribe(
          res => {
            if (res.status) {
              this.toastrService.success(res.message);
              res.supplier = res.data; //  @TODO
              res.supplier.name = res.data.companyName; //  @TODO
              this.onClose(res);
              return;
            }
            // this.toastrService.error(this.translateService.instant('common.errors.unknown_server_error'));
          },
          err => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }

  /**
   * Handle serach supplier data from externla search
   */
  onExternalSearch(): void {
    if (this.model.vatNumber || this.model.name) {
      this.dialogIsLoading = true;
      this.suppliersFromOpenApi = [];

      this.dataFromOpenApi = true;
      if (this.model.vatNumber) {
        this.subscriptions.push(
          this.creditsafeService.getCompanyByVatNumber(this.model.vatNumber).subscribe(
            res => {
              if (res && res.status && res.data) {
                const items = [];
                items.push({
                  companyName: res.data.name,
                  vatCode: (Array.isArray(res.data.vatNo) && res.data.vatNo.length) ? res.data.vatNo[0]: res.data.vatNo,
                  address: res.data.address,
                  phone: (Array.isArray(res.data.phoneNumbers) && res.data.phoneNumbers.length) ? res.data.phoneNumbers[0]: '',
                });
                this.suppliersFromOpenApi = items;
              }
              this.dialogIsLoading = false;
            },
            err => {
              this.dialogIsLoading = false;
              this.helperService.handleError(err);
            }
          )
        );
      } else if (this.model.name) {
        this.subscriptions.push(
          this.creditsafeService.getCompanyByCompanyName(this.model.name).subscribe(
            res => {
              if (res && res.status && res.data && Array.isArray(res.data)) {
                const items = [];
                for (const item of res.data) {
                  items.push({
                    companyName: item.name,
                    vatCode: (Array.isArray(item.vatNo) && item.vatNo.length) ? item.vatNo[0]: item.vatNo,
                    address: item.address,
                    phone: (Array.isArray(item.phoneNumbers) && item.phoneNumbers.length) ? item.phoneNumbers[0]: '',
                  });
                }
                this.suppliersFromOpenApi = items;
              }
              this.dialogIsLoading = false;
            },
            err => {
              this.dialogIsLoading = false;
              this.helperService.handleError(err);
            }
          )
        );
      }
    } 
  }

  // /**
  //  * Handle serach supplier data from externla search
  //  */
  // onExternalSearch(): void {
  //   if (this.model.vatNumber || this.model.name) {
  //     this.dialogIsLoading = true;
  //     this.suppliersFromOpenApi = [];

  //     let token = '';
  //     this.dataFromOpenApi = true;
  //     this.subscriptions.push(
  //       this.openapiService.getValidToken().subscribe(
  //         res => {
  //           token = res;
  //           if (this.model.vatNumber) {
  //             this.subscriptions.push(
  //               this.openapiService.getCompanyByVatNumber(token, this.model.vatNumber).subscribe(
  //                 res => {
  //                   if (res) {
  //                     this.suppliersFromOpenApi = res;
  //                   }
  //                   this.dialogIsLoading = false;
  //                 },
  //                 err => {
  //                   this.dialogIsLoading = false;
  //                   this.helperService.handleError(err);
  //                 }
  //               )
  //             );
  //           } else if (this.model.name) {
  //             this.subscriptions.push(
  //               this.openapiService.getCompanyByCompanyName(token, this.model.name).subscribe(
  //                 res => {
  //                   if (res) {
  //                     this.suppliersFromOpenApi = res;
  //                   }
  //                   this.dialogIsLoading = false;
  //                 },
  //                 err => {
  //                   this.dialogIsLoading = false;
  //                   this.helperService.handleError(err);
  //                 }
  //               )
  //             );
  //           }
  //         },
  //         err => {
  //           this.dialogIsLoading = false;
  //           this.helperService.handleError(err);
  //         }
  //       )
  //     );
  //   } 
  // }

  /**
   * Handle select supplier
   * @param item 
   */
  onSelectedItem(item: any): void {
    const nameControl = this.form.controls.name;
    const vatNumberControl = this.form.controls.vatNumber;
    const taxCodeControl = this.form.controls.taxCode;
    const legalAddressControl = this.form.controls.legalAddress;
    const phoneControl = this.form.controls.phone;
    const openApiIdControl = this.form.controls.openApiId;
    const pecControl = this.form.controls.pec;
    const startCompanyDateControl = this.form.controls.startCompanyDate;
    const statusControl = this.form.controls.status;
    const notRegisteredControl = this.form.controls.notRegistered;

    // 'sdiCode', item.sdiCode

    nameControl.setValue(item.companyName);
    vatNumberControl.setValue(item.vatCode ? item.vatCode : '');
    taxCodeControl.setValue(item.taxCode ? item.taxCode : '');
    phoneControl.setValue(item.phone ? item.phone : '');
  
    if (item.address){
      const address = {
        route: item.address.street,
        streetNumber: item.address.houseNo,
        postalCode: item.address.postCode,
        city: item.address.city,
        administrativeArea1: '',
        administrativeArea2: item.address.province,
        administrativeArea3: item.address.city,
        countryCodeTwo: "IT",
      }
      legalAddressControl.setValue(address);
    }
    // openApiIdControl.setValue(item.id);
    // pecControl.setValue(item.pec);
    // if (moment(item.startDate).isValid()) {
    //   startCompanyDateControl.setValue(`${item.startDate}T00:00:00.000Z`);
    // }

    // switch (item.activityStatus) {
    //   case 'ATTIVA': statusControl.setValue('active'); break;
    //   case 'REGISTRATA': statusControl.setValue('registered'); break;
    //   case 'INATTIVA': statusControl.setValue('inactive'); break;
    //   case 'SOSPESA': statusControl.setValue('suspended'); break;
    //   case 'IN_ISCRIZIONE': statusControl.setValue('inRegistration'); break;
    //   case 'CESSATA': statusControl.setValue('ceased'); break;
    //   default: {
    //     statusControl.setValue('notDefined');
    //     notRegisteredControl.setValue(true);
    //   }
    // }
  }
}
